import AIChatWidget from '@/components/aichat/AiChatWidget';
import ContentFeatureCard from '@/components/landing/content-feature-card';
import SeoHead from '@/components/seo/SeoHead';
import { TablePagination } from '@/components/table-components';
import LandingLayout from '@/layouts/landing-layout';
import { Link, router } from '@inertiajs/react';
import { ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

const baseUrl = import.meta.env.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');
const PER_PAGE = 8;

type ContentItem = {
    id: number;
    title: string;
    description?: string | null;
    category?: string | null;
    date?: string | null;
    url?: string | null;
    image?: string | null;
    institute?: string | null;
    venue?: string | null;
    status?: string | null;
    sdgs?: Array<{
        sdg_number: number;
        short_title: string;
        slug: string;
        image?: string | null;
    }>;
};

type Props = {
    sdg: {
        id: number;
        sdg_number: number;
        title: string;
        short_title: string;
        description: string;
        icon?: string | null;
        color?: string | null;
        image?: string | null;
        slug: string;
        counts: {
            news: number;
            events: number;
            researches: number;
            activities: number;
            reports: number;
            total: number;
        };
    };
    researches: string;
    researchPagination: string;
    news: string;
    newsPagination: string;
    events: ContentItem[];
    activities: string;
    activityPagination: string;
    reports: ContentItem[];
    tab: string;
};

type PaginationState = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
};

function ContentSection({ title, items }: { title: string; items: ContentItem[] }) {
    return (
        <section>
            <div className="mb-8 text-center">
                <h2 className="text-foreground mb-2 text-2xl font-semibold tracking-tight md:text-3xl">{title}</h2>
                <p className="text-muted-foreground text-sm md:text-base">
                    {items.length} {items.length === 1 ? 'item' : 'items'} tagged under this sustainable development goal.
                </p>
            </div>

            {items.length === 0 ? (
                <div className="bg-card border-border flex flex-col items-center justify-center rounded-2xl border px-4 py-20 text-center shadow-sm">
                    <p className="text-muted-foreground text-sm md:text-base">No tagged content yet for this section.</p>
                </div>
            ) : (
                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
                    {items.map((item) => (
                        <ContentFeatureCard
                            key={item.id}
                            title={item.title}
                            image={item.image}
                            institute={item.institute}
                            badges={[item.category, item.status].filter((badge): badge is string => Boolean(badge))}
                            description={item.description}
                            date={item.date}
                            linkHref={item.url}
                            sdgs={item.sdgs}
                        />
                    ))}
                </div>
            )}
        </section>
    );
}

export default function SdgDetails({
    sdg,
    researches: encodedResearches,
    researchPagination: encodedResearchPagination,
    news: encodedNews,
    newsPagination: encodedNewsPagination,
    activities: encodedActivities,
    activityPagination: encodedActivityPagination,
    tab,
}: Props) {
    const researches = useMemo<ContentItem[]>(() => {
        try {
            return JSON.parse(atob(encodedResearches));
        } catch {
            return [];
        }
    }, [encodedResearches]);

    const researchPagination = useMemo<PaginationState>(() => {
        try {
            const pagination = JSON.parse(atob(encodedResearchPagination));
            const lastPage = pagination.per_page > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.per_page)) : 1;

            return { ...pagination, last_page: lastPage };
        } catch {
            return { current_page: 1, per_page: PER_PAGE, last_page: 1, total: 0 };
        }
    }, [encodedResearchPagination]);

    const news = useMemo<ContentItem[]>(() => {
        try {
            return JSON.parse(atob(encodedNews));
        } catch {
            return [];
        }
    }, [encodedNews]);

    const newsPagination = useMemo<PaginationState>(() => {
        try {
            const pagination = JSON.parse(atob(encodedNewsPagination));
            const lastPage = pagination.per_page > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.per_page)) : 1;

            return { ...pagination, last_page: lastPage };
        } catch {
            return { current_page: 1, per_page: PER_PAGE, last_page: 1, total: 0 };
        }
    }, [encodedNewsPagination]);

    const activities = useMemo<ContentItem[]>(() => {
        try {
            return JSON.parse(atob(encodedActivities));
        } catch {
            return [];
        }
    }, [encodedActivities]);

    const activityPagination = useMemo<PaginationState>(() => {
        try {
            const pagination = JSON.parse(atob(encodedActivityPagination));
            const lastPage = pagination.per_page > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.per_page)) : 1;

            return { ...pagination, last_page: lastPage };
        } catch {
            return { current_page: 1, per_page: PER_PAGE, last_page: 1, total: 0 };
        }
    }, [encodedActivityPagination]);

    const tabs = [
        { key: 'news', label: 'News', title: 'News', items: news },
        { key: 'research', label: 'Research', title: 'Research', items: researches },
        { key: 'activities', label: 'Activities', title: 'Institutional Activities', items: activities },
    ] as const;

    const [activeTab, setActiveTab] = useState<(typeof tabs)[number]['key']>(tab === 'research' || tab === 'activities' ? tab : 'news');
    const activeSection = tabs.find((tab) => tab.key === activeTab) ?? tabs[0];
    const heroImage = sdg.image || '/images/hero/nc1.png';
    const seoImage = sdg.image || `${baseUrl}/images/logo.webp`;
    const newsMeta = useMemo(() => {
        const from = newsPagination.total === 0 ? 0 : (newsPagination.current_page - 1) * newsPagination.per_page + 1;
        const to = newsPagination.total === 0 ? 0 : Math.min(newsPagination.total, newsPagination.current_page * newsPagination.per_page);

        return { from, to };
    }, [newsPagination]);
    const researchMeta = useMemo(() => {
        const from = researchPagination.total === 0 ? 0 : (researchPagination.current_page - 1) * researchPagination.per_page + 1;
        const to =
            researchPagination.total === 0 ? 0 : Math.min(researchPagination.total, researchPagination.current_page * researchPagination.per_page);

        return { from, to };
    }, [researchPagination]);
    const activityMeta = useMemo(() => {
        const from = activityPagination.total === 0 ? 0 : (activityPagination.current_page - 1) * activityPagination.per_page + 1;
        const to =
            activityPagination.total === 0 ? 0 : Math.min(activityPagination.total, activityPagination.current_page * activityPagination.per_page);

        return { from, to };
    }, [activityPagination]);

    const visitSdgPage = (nextTab: 'news' | 'research' | 'activities', overrides: Partial<Record<'news_page' | 'research_page' | 'activity_page', number>>) => {
        router.get(
            route('sdg.show', { sdg: sdg.slug }),
            {
                tab: nextTab,
                news_page: overrides.news_page ?? newsPagination.current_page,
                research_page: overrides.research_page ?? researchPagination.current_page,
                activity_page: overrides.activity_page ?? activityPagination.current_page,
            },
            {
                preserveScroll: true,
                preserveState: true,
                replace: true,
            },
        );
    };

    const handleChangeTab = (nextTab: 'news' | 'research' | 'activities') => {
        setActiveTab(nextTab);
        visitSdgPage(nextTab, {});
    };

    const handleNewsPageChange = (page: number) => {
        visitSdgPage('news', { news_page: page });
    };

    const handleResearchPageChange = (page: number) => {
        visitSdgPage('research', { research_page: page });
    };

    const handleActivityPageChange = (page: number) => {
        visitSdgPage('activities', { activity_page: page });
    };

    return (
        <LandingLayout>
            <SeoHead title={`SDG ${sdg.sdg_number} - ${sdg.short_title}`} description={sdg.description} image={seoImage} />

            <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                <img src={heroImage} alt={sdg.title} className="absolute inset-0 h-full w-full scale-105 object-cover opacity-45" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
                <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-emerald-500/5 blur-[100px]" />

                <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                    <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                        <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                            Home
                        </Link>
                        <ChevronRight className="size-4" aria-hidden="true" />
                        <Link href={route('sustainableDevelopmentGoals')} prefetch className="transition-colors hover:text-emerald-300">
                            Sustainable Development Goals
                        </Link>
                        <ChevronRight className="size-4" aria-hidden="true" />
                        <span className="text-white/80">SDG {sdg.sdg_number}</span>
                    </div>

                    <div className="flex flex-col items-center gap-6 text-center md:flex-row md:text-left">
                        {sdg.image ? (
                            <div className="border-border flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-3xl border bg-white p-3 shadow-lg md:h-28 md:w-28">
                                <img src={sdg.image} alt={`SDG ${sdg.sdg_number}`} className="h-full w-full object-contain" />
                            </div>
                        ) : null}
                        <div>
                            <p className="text-sm font-semibold tracking-[0.28em] text-[#21d36b] uppercase">Sustainable Development Goal {sdg.sdg_number}</p>
                            <h1 className="mt-3 max-w-5xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                                {sdg.title}
                            </h1>
                            <p className="mt-4 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">{sdg.description}</p>
                        </div>
                    </div>
                </div>
            </div>

            <section className="bg-muted relative z-20 py-12">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="relative z-20 mx-auto -mt-20 mb-10 max-w-7xl">
                        <div className="bg-card border-border rounded-2xl border p-4 shadow-sm md:p-6">
                            <div className="flex flex-wrap justify-center gap-2">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        onClick={() => handleChangeTab(tab.key)}
                                        className={`flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 md:px-5 md:py-2.5 md:text-sm ${
                                            activeTab === tab.key
                                                ? 'bg-[#0A7A35] text-white shadow-md'
                                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <ContentSection title={activeSection.title} items={activeSection.items} />

                    {activeTab === 'news' && newsPagination.total > 0 ? (
                        <div className="mt-6">
                            <TablePagination
                                hidePerPage
                                currentPage={newsPagination.current_page}
                                lastPage={newsPagination.last_page}
                                perPage={newsPagination.per_page}
                                total={newsPagination.total}
                                from={newsMeta.from}
                                to={newsMeta.to}
                                onPageChange={(page) => handleNewsPageChange(page)}
                                onPerPageChange={() => {}}
                            />
                        </div>
                    ) : null}

                    {activeTab === 'research' && researchPagination.total > 0 ? (
                        <div className="mt-6">
                            <TablePagination
                                hidePerPage
                                currentPage={researchPagination.current_page}
                                lastPage={researchPagination.last_page}
                                perPage={researchPagination.per_page}
                                total={researchPagination.total}
                                from={researchMeta.from}
                                to={researchMeta.to}
                                onPageChange={(page) => handleResearchPageChange(page)}
                                onPerPageChange={() => {}}
                            />
                        </div>
                    ) : null}

                    {activeTab === 'activities' && activityPagination.total > 0 ? (
                        <div className="mt-6">
                            <TablePagination
                                hidePerPage
                                currentPage={activityPagination.current_page}
                                lastPage={activityPagination.last_page}
                                perPage={activityPagination.per_page}
                                total={activityPagination.total}
                                from={activityMeta.from}
                                to={activityMeta.to}
                                onPageChange={(page) => handleActivityPageChange(page)}
                                onPerPageChange={() => {}}
                            />
                        </div>
                    ) : null}
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
